import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';
import { authService } from '../../services/api';

const syncTokensFromStorage = (state) => {
  state.tokens = {
    accessToken: localStorage.getItem('access_token') || null,
    refreshToken: localStorage.getItem('refresh_token') || null,
    accessTokenExpiresAt: localStorage.getItem('access_token_expires_at') || null,
    refreshTokenExpiresAt: localStorage.getItem('refresh_token_expires') || null,
  };
};

const checkInitialAuth = () => {
  const accessToken = localStorage.getItem('access_token');
  const refreshToken = localStorage.getItem('refresh_token');
  return !!accessToken && !!refreshToken;
};

const initialState = {
  isAuthenticated: checkInitialAuth(),
  authInitialized: false,
  user: null,
  loading: false,
  error: null,
  otpSent: false,
  currentUser: null,
  userType: localStorage.getItem('user_type') || 'parents',
  tokens: {
    accessToken: localStorage.getItem('access_token') || null,
    refreshToken: localStorage.getItem('refresh_token') || null,
    accessTokenExpiresAt: localStorage.getItem('access_token_expires_at') || null,
    refreshTokenExpiresAt: localStorage.getItem('refresh_token_expires') || null,
  },
};

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await authService.login(email);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const verifyOtp = createAsyncThunk(
  'auth/verifyOtp',
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const response = await authService.verifyOtp(email, otp);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authService.logout();
      return true;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.refreshToken();
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Échec du rafraîchissement du token');
    }
  }
);

export const initializeAuth = createAsyncThunk(
  'auth/initializeAuth',
  async (_, { rejectWithValue }) => {
    try {
      const expiresAt = localStorage.getItem('access_token_expires_at');
      const shouldRefreshFirst = !expiresAt
        || parseInt(expiresAt, 10) - Date.now() < 60000;

      if (shouldRefreshFirst && localStorage.getItem('refresh_token')) {
        await authService.refreshToken();
      }

      const meResponse = await authService.getMe();
      return meResponse;
    } catch (error) {
      if (localStorage.getItem('refresh_token')) {
        try {
          await authService.refreshToken();
          const meResponse = await authService.getMe();
          return meResponse;
        } catch (retryError) {
          return rejectWithValue(
            retryError.message || error.message || 'Impossible de restaurer la session'
          );
        }
      }
      return rejectWithValue(error.message || 'Impossible de restaurer la session');
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'auth/updateUserProfile',
  async (userData, { getState, rejectWithValue }) => {
    try {
      const { user } = getState().auth;
      if (!user?.id) {
        return rejectWithValue('Utilisateur non connecté');
      }

      const updatedUser = await authService.updateProfile(user.id, userData);
      return updatedUser;
    } catch (error) {
      return rejectWithValue(error.message || 'Impossible de mettre à jour le profil');
    }
  }
);

const resetState = (state) => {
  state.isAuthenticated = false;
  state.user = null;
  state.currentUser = null;
  state.otpSent = false;
  state.userType = 'parents';
  state.tokens = {
    accessToken: null,
    refreshToken: null,
    accessTokenExpiresAt: null,
    refreshTokenExpiresAt: null,
  };
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetOtpFlow: (state) => {
      state.otpSent = false;
      state.loading = false;
      state.error = null;
    },
    resetAuth: (state) => {
      resetState(state);
    },
    setAuthInitialized: (state) => {
      state.authInitialized = true;
    },
    updateUserType: (state, action) => {
      state.userType = action.payload;
      localStorage.setItem('user_type', action.payload);
      if (state.user) {
        state.user.user_type = action.payload;
      }
      if (state.currentUser) {
        state.currentUser.user_type = action.payload;
      }
    },
    forceLogout: (state) => {
      resetState(state);
      state.error = null;
      state.authInitialized = true;
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('access_token_expires_at');
      localStorage.removeItem('refresh_token_expires');
      localStorage.removeItem('user');
      localStorage.removeItem('user_type');
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(REHYDRATE, (state) => {
        state.authInitialized = false;
        state.isAuthenticated = checkInitialAuth();
        syncTokensFromStorage(state);
      })
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state) => {
        state.loading = false;
        state.otpSent = true;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.otpSent = false;
      })
      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.authInitialized = true;
        state.user = action.payload.data;
        state.currentUser = action.payload.data;
        state.userType = action.payload.data?.user_type || 'parents';
        localStorage.setItem('user_type', state.userType);
        state.otpSent = false;
        state.error = null;
        syncTokensFromStorage(state);
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(logout.pending, (state) => {
        state.loading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        state.authInitialized = true;
        resetState(state);
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || null;
        state.authInitialized = true;
        resetState(state);
      })
      .addCase(refreshToken.pending, (state) => {
        state.loading = true;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.tokens) {
          state.isAuthenticated = true;
          syncTokensFromStorage(state);
        } else {
          resetState(state);
        }
      })
      .addCase(refreshToken.rejected, (state) => {
        state.loading = false;
        resetState(state);
      })
      .addCase(initializeAuth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.authInitialized = true;
        state.isAuthenticated = true;
        state.user = action.payload.data;
        state.currentUser = action.payload.data;
        state.userType = action.payload.data?.user_type || state.userType;
        if (action.payload.data?.user_type) {
          localStorage.setItem('user_type', action.payload.data.user_type);
        }
        syncTokensFromStorage(state);
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.loading = false;
        state.authInitialized = true;
        resetState(state);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('access_token_expires_at');
        localStorage.removeItem('refresh_token_expires');
        localStorage.removeItem('user');
      })
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.user = action.payload;
        state.currentUser = action.payload;
        state.userType = action.payload?.user_type || state.userType;
        if (action.payload?.user_type) {
          localStorage.setItem('user_type', action.payload.user_type);
        }
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, resetOtpFlow, resetAuth, updateUserType, forceLogout, setAuthInitialized } = authSlice.actions;

export default authSlice.reducer;
